import {
  ProductDetail,
  AvailabilityRequest,
  AvailabilityResult,
  RetailOption,
  TimeSlot,
  VATRule,
  VATType,
  ConfigurableDataProvider,
  RestApiConfig,
  AddonDisplayStep,
  AddonProductConfig,
  PriceAdditionalInfo,
  TimeslotsByDateRangeRequest,
  TimeslotWithAvailability,
  QrCodeType,
} from "@smeetz/orders";
import POSConfigModule from "@/store/modules/pos-config";
import { getRetailOptionsByUUIDs } from "@/utils/helpers/product-category";
import {
  getProductCategoriesWithFilteredPrices,
  getProductCategoriesByPriceUuids,
  getProductCategoryByPriceId,
  getPriceByUuid,
} from "@/dexie/product-category";
import {
  getTimeslotsByIds,
  getTimeslotByPriceAndId,
  queryTimeslotsByProductCategories,
} from "@/dexie/timeslots";
import { getTaxesFromPrices } from "@/db/tax";
import { ITimeSlot } from "@/types/db/timeslots";
import { countPendingOrderQuantity } from "@/utils/vue-helpers/order-stock.helper";
import { getPcStockUnits } from "@/utils/helpers/stock";
import { mapPcTypeToOrderLineItemType } from "@/utils/helpers/orders";
import {
  IPackagePrice,
  IProductCategory,
  IProductSlotPrice,
  PCTypes,
} from "@/types/db/product-category";

function mapToProductDetail(
  pc: IProductCategory,
  price: IProductSlotPrice,
  priceAdditionalInfo?: PriceAdditionalInfo
): ProductDetail {
  const tax = price.taxes?.[0];
  const vatRate = tax?.rate
    ? (Number(tax.rate) || 0) / 100
    : (tax?.rateMiliValue || 0) / 1_000_000;

  const groupId = POSConfigModule.groupInfo?.id ?? undefined;
  const tokenLength =
    POSConfigModule.groupInfo?.itemTokenSettingList?.find(
      (setting) => setting?.groupId === groupId
    )?.tokenLength ?? undefined;

  return {
    productId: String(pc.product.uuid),
    categoryId: pc.uuid,
    priceId: price.uuid,
    categoryName: pc.name,
    categoryType: mapPcTypeToOrderLineItemType(pc.type),
    priceName: price.name || pc.name,
    retailOptions: price.retail.map((retail) => ({
      priceId: price.uuid,
      retailOptionId: retail.uuid,
      name: retail.name || price.name,
      unitPrice: price.value / 100,
      currency: pc.product.currency,
      // Deleted options are filtered out at sync, so local ones are always active
      isActive: true,
      sellingSoldOut: retail.sellingSoldOut === 1,
      optionType: retail.type,
      // inventory: {
      //   stockQuantity: price.quantity,
      //   // trackStock: true,
      // },
    })),
    priceAdditionalInfo,
    unitPrice: price.value / 100,
    currency: pc.product.currency,
    vatId: price.vatRateGeneralId,
    vatType: tax
      ? tax.includedInPrice
        ? VATType.INCLUDED
        : VATType.EXCLUDED
      : VATType.INCLUDED,
    vatRate,
    vatCode: tax?.taxNumber,
    isActive: !price.cancelled,
    qrCodeType: (pc.qrCodeType ?? undefined) as QrCodeType | undefined,
    tokenLength,
    groupId,
    ticketTypeCode: pc.handshakeCategoryTsproperty?.ticketTypeCode,
    areaCode: pc.handshakeCategoryTsproperty?.areaCode,
    multipleScan: pc.multipleScan ?? undefined,
    oneTokenVoucherTickets:
      POSConfigModule.groupInfo?.oneTokenVoucherTickets === 1,
    metadata: {
      description: price.description,
      tags: [],
      minQuantity: price.minBooking || 1,
      maxQuantity: price.maxBooking || 100,
    },
  };
}

async function mapAddonToDetail(
  addon: IPackagePrice
): Promise<AddonProductConfig | null> {
  const pc = await getProductCategoryByPriceId(addon.priceId);
  if (!pc) {
    return null;
  }
  const price = pc?.prices.find(
    (p) => p.id === addon.priceId
  ) as IProductSlotPrice;

  return {
    id: addon.id,
    product: mapToProductDetail(pc, price),
    sendTicket: addon.sendTicket === 1,
    displayPriority: addon.priority || 5,
    step:
      addon.displayPage === 1
        ? AddonDisplayStep.FIRST_SCREEN
        : AddonDisplayStep.INTERMEDIARY_PAGE,
    hidden: addon.automaticallyBook === 1,
    mandatory: addon.isMandatory === 1,
    minQuantity: addon.minQuantity,
    maxQuantity: addon.maxQuantity,
    includedInTicketPrice: addon.displayedValue === 0,
    customerDisplayedPrice: (addon.displayedValue || 0) / 100,
    useAddonDateTime:
      addon.isUseAddonDateTime != null
        ? addon.isUseAddonDateTime === 1
        : undefined,
    calendarVisibility: {
      minutesBefore: addon.minutesBefore,
      minutesAfter: addon.minutesAfter,
      minutesBeforeStartMin: addon.minutesBeforeStartMin,
      minutesBeforeStartMax: addon.minutesBeforeStartMax,
      minutesAfterStartMin: addon.minutesAfterStartMin,
      minutesAfterStartMax: addon.minutesAfterStartMax,
    },
  };
}

export class OrderDataProvider extends ConfigurableDataProvider {
  constructor(restConfig: RestApiConfig) {
    super(restConfig);
  }

  async fetchProductDetails(priceUUIDs: string[]): Promise<ProductDetail[]> {
    const productCategories = await getProductCategoriesWithFilteredPrices(
      priceUUIDs.map((uuid) => uuid)
    );

    return await Promise.all(
      productCategories.flatMap((pc) =>
        pc.prices.map(async (price): Promise<ProductDetail> => {
          const autoAddons = (
            await Promise.all(
              price.addons
                .filter(
                  (addon) =>
                    addon.canceled !== 1 && addon.automaticallyBook === 1
                )
                .map(mapAddonToDetail)
            )
          ).filter((a): a is AddonProductConfig => a !== null);

          const selectableAddons = (
            await Promise.all(
              price.addons
                .filter(
                  (addon) =>
                    addon.canceled !== 1 && addon.automaticallyBook !== 1
                )
                .map(mapAddonToDetail)
            )
          ).filter((a): a is AddonProductConfig => a !== null);

          return mapToProductDetail(pc, price, {
            globalAddonSettings: {
              limitTotalQuantity: {
                enabled:
                  (price.packageMinQuantity ?? 0) > 0 ||
                  (price.packageMaxQuantity ?? 0) > 0,
                minTotalAddons: price.packageMinQuantity || 0,
                maxTotalAddons: price.packageMaxQuantity || undefined,
              },
            },
            autoAddons,
            selectableAddons,
          });
        })
      )
    );
  }

  async fetchRetailOptions(
    retailOptionUUIDs: string[]
  ): Promise<RetailOption[]> {
    const results = await getRetailOptionsByUUIDs(
      retailOptionUUIDs.map((uuid) => uuid)
    );

    return results.map(({ pc, price, retailOption }): RetailOption => {
      return {
        priceId: price.uuid,
        retailOptionId: retailOption.uuid,
        name: retailOption.name || price.name,
        unitPrice: price.value / 100,
        currency: pc.product.currency,
        // Deleted options are filtered out at sync, so local ones are always active
        isActive: true,
        sellingSoldOut: retailOption.sellingSoldOut === 1,
        optionType: retailOption.type,
        // inventory: {
        //   stockQuantity: price.quantity,
        //   trackStock: true,
        // },
      };
    });
  }

  async fetchTimeSlots(timeslotIds: number[]): Promise<TimeSlot[]> {
    const timeslots = await getTimeslotsByIds(timeslotIds);

    return timeslots.map((ts): TimeSlot => {
      const startTime = new Date(ts.startDateTime);
      const endTime = new Date(ts.endDateTime);

      return {
        timeSlotId: ts.id,
        startTime,
        endTime,
        isActive: new Date() <= endTime,
        capacity: ts.availability + ts.booked,
        bookedQuantity: ts.booked,
        allowOverbooking: false,
      };
    });
  }

  async fetchTimeslotsByDateRange(
    request: TimeslotsByDateRangeRequest
  ): Promise<TimeslotWithAvailability[]> {
    const [pc] = await getProductCategoriesByPriceUuids([request.priceId]);
    if (!pc) {
      throw new Error(`Price with ID ${request.priceId} not found`);
    }

    // Seated sub-category timeslots are stored under the seating parent PSC
    const isSeated =
      pc.type === PCTypes[PCTypes.Seated] ||
      pc.type === PCTypes[PCTypes.SeatedSub];
    const categoryId =
      isSeated && pc.seatingParentPSCId != null ? pc.seatingParentPSCId : pc.id;

    const timeslots = await queryTimeslotsByProductCategories({
      categoryIds: [categoryId],
      startDateTimeAfter: request.fromDateTime.toISOString(),
      startDateTimeBefore: request.toDateTime.toISOString(),
    });

    const minAvailableQuantity = request.minAvailableQuantity ?? 0;

    return timeslots
      .filter(
        (ts) => Boolean(ts.bookable) && ts.availability >= minAvailableQuantity
      )
      .map((ts): TimeslotWithAvailability => {
        const endTime = new Date(ts.endDateTime);

        return {
          timeSlot: {
            timeSlotId: ts.id,
            startTime: new Date(ts.startDateTime),
            endTime,
            isActive: new Date() <= endTime,
            capacity: ts.availability + ts.booked,
            bookedQuantity: ts.booked,
            allowOverbooking: false,
          },
          availableQuantity: ts.availability,
          isAvailable: ts.availability > 0,
        };
      });
  }

  async checkAvailability(
    request: AvailabilityRequest
  ): Promise<AvailabilityResult> {
    // If timeSlotId is provided, check timeslot-based availability
    if (request.timeSlotId) {
      const timeslot = await getTimeslotByPriceAndId(request);
      if (!timeslot) {
        return {
          priceId: request.priceId,
          isAvailable: false,
          availableQuantity: 0,
          isOfflineData: true,
        };
      }

      const availableQuantity =
        timeslot.availability -
        (await this.pendingQuantityFor(request, timeslot));
      return {
        priceId: request.priceId,
        isAvailable:
          Boolean(timeslot.bookable) &&
          availableQuantity >= (request.quantity || 1),
        availableQuantity,
        isOfflineData: true,
      };
    }

    // Otherwise, fall back to price-based availability
    const price = await getPriceByUuid(request.priceId);
    if (!price) {
      return {
        priceId: request.priceId,
        isAvailable: false,
        availableQuantity: 0,
        isOfflineData: true,
      };
    }

    const retail = price.retail.length > 0 ? price.retail[0] : null;

    const availableQuantity =
      price.quantity - (await this.pendingQuantityFor(request));
    return {
      priceId: request.priceId,
      isAvailable: availableQuantity >= (request.quantity || 1),
      availableQuantity,
      sellingSoldOut: retail?.sellingSoldOut === 1,
      isOfflineData: true,
    };
  }

  /**
   * Quantity already pending in the selected order for the same stock as the
   * requested price/timeslot. The catalog numbers never include pending
   * lines, so subtracting the full pending count is also correct for the
   * package's quantity-increase checks, which pass only the increase diff.
   */
  private async pendingQuantityFor(
    request: AvailabilityRequest,
    timeslot?: ITimeSlot
  ): Promise<number> {
    const [pc] = await getProductCategoriesByPriceUuids([request.priceId]);
    return countPendingOrderQuantity({
      stockIds: pc?.stockIds ?? [],
      stockUnits: getPcStockUnits(pc?.stockConsumptions),
      priceUuids: [request.priceId],
      timeSlot: timeslot
        ? {
            id: timeslot.id,
            startDateTime: String(timeslot.startDateTime),
            endDateTime: String(timeslot.endDateTime),
          }
        : undefined,
    });
  }

  async checkBatchAvailability(
    requests: AvailabilityRequest[]
  ): Promise<AvailabilityResult[]> {
    return Promise.all(requests.map((r) => this.checkAvailability(r)));
  }

  async fetchVATRules(priceUUIDs: string[]): Promise<VATRule[]> {
    const taxes = await getTaxesFromPrices(priceUUIDs.map((uuid) => uuid));

    return taxes
      .filter((tax) => !tax.deletedAt)
      .map((tax) => ({
        priceId: tax.priceUuid,
        vatRate: tax.rate
          ? (Number(tax.rate) || 0) / 100
          : (Number(tax.rateMiliValue) || 0) / 1_000_000,
        vatCode: tax.taxNumber,
        description: String(tax.name),
        isActive: !tax.deletedAt,
      }));
  }
}
